import { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRotateLeft,
  faCheck,
  faDollarSign,
  faFaceSmile,
  faStar,
} from '@fortawesome/pro-regular-svg-icons';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ButtonLink from '@v2/shared/components/ButtonLink';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';
import { cn } from '@v2/shared/lib/utils';
import useCurrentYear from '@v2/shared/lib/useCurrentYear';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { liveAgentClock, liveAgentHours } from '@v2/shared/lib/supportHours';

/** The year DiscountMags opened, which the H1 counts from rather than stating a total. */
const FOUNDED_YEAR = 2002;

export type Props = {
  /**
   * What customers rate us, read through the same client and the same function /reviews
   * uses so the two pages cannot disagree. Null when SmartCustomer could not be reached,
   * which is a real state this page renders in — never a zero.
   */
  reviews: { averageRating: number; reviewCount: number } | null;
  /**
   * The year the server rendered, which the H1 counts from `FOUNDED_YEAR`. Handed to
   * `useCurrentYear`, which corrects it in the browser — this page's markup is cached
   * for a day, so a year the component worked out for itself would disagree with a
   * day-old render and cost a repaint.
   */
  currentYear?: number;
};

/** A link in body copy, which has no `prose` here to colour it. */
const LINK = 'text-primary-700 underline underline-offset-2';

/**
 * The stat strip's inset from the column's edge, so the hero shows either side of it
 * where it overlaps. Only the strip: everything below spans the full column, as on
 * /refund-policy and /guarantee.
 */
const GUTTER = 'mx-4 md:mx-7';

/** A section of the page: the distance from the one above it. */
const BLOCK = 'mt-6';

/** `rounded-xl` rather than ContentCard's `rounded-lg`, to match the hero above them. */
const CARD = 'rounded-xl';

const SECTION_HEADING = 'm-0 mb-1 text-2xl font-extrabold tracking-tight text-slate-900';

/** The line under a section heading that says what the section is for. */
const TAGLINE = 'mb-3.5 mt-0 text-sm text-slate-500';

/** Clears the fixed header when a `#` link lands on a section. */
const ANCHORED = 'scroll-mt-14 md:scroll-mt-32 xl:scroll-mt-24';

const BODY = 'mb-3 mt-0 max-w-[70ch] text-slate-800';

/** A tinted box used for a timeline entry and for a team fact. */
const TILE = 'rounded-lg border border-solid border-slate-200 bg-slate-50 px-4 py-3.5';

/** The label above a figure in one of those boxes. */
const TILE_LABEL = 'text-[11px] font-bold uppercase tracking-wider';

const JUMP_LINKS = [
  { href: '#story', label: 'Our story' },
  { href: '#different', label: "Why we're different" },
  { href: '#team', label: 'Our team' },
  { href: '#promise', label: 'Our promise' },
  { href: '#contact', label: 'Get in touch' },
  { href: '#faqs', label: 'FAQs' },
];

const TIMELINE = [
  {
    year: '2002',
    title: 'DiscountMags opens',
    detail:
      'Print magazine subscriptions at the lowest price we could negotiate, with a real refund policy.',
  },
  {
    year: '2016',
    title: 'Digital magazines',
    detail: 'Digital editions delivered through Zinio and Magzter, alongside print.',
  },
  {
    year: '2023',
    title: 'Books and more',
    detail:
      'Books, calendars and other print goods. Today: 1,000,000+ titles, still family owned, still a small team.',
  },
];

const TEAM_FACTS = [
  { label: 'Team size', value: 'About a dozen', note: 'including the family that owns it' },
  { label: 'Since', value: '2002', note: 'same family, same mission' },
  {
    label: 'Who answers you',
    value: 'Our own team',
    note: 'the same people who run the business',
  },
];

const FAQS: { question: string; answer: (urls: Record<string, string>) => ReactNode }[] = [
  {
    question: 'Is DiscountMags legit?',
    answer: (urls) => (
      <>
        Yes. We&apos;re a family owned small business operating since 2002, based in the United
        States. Our{' '}
        <a className={LINK} href={urls.reviews}>
          customer reviews
        </a>{' '}
        are independently collected and verified by SmartCustomer from confirmed buyers, and every
        order is backed by our{' '}
        <a className={LINK} href={urls.guarantee}>
          satisfaction guarantee
        </a>
        .
      </>
    ),
  },
  {
    question: 'Are you the publisher of the magazines you sell?',
    answer: () => (
      <>
        No. We&apos;re an authorized subscription retailer. Publishers print and mail the magazines;
        we sell the subscriptions at a discount and handle your order, your account and your
        refunds. This site is not officially affiliated with, associated with, or endorsed by any
        magazine or publisher. Magazine names, logos, and images may be registered trademarks of
        individual magazines, publishers, or parent companies.
      </>
    ),
  },
  {
    question: 'How are you able to sell subscriptions for less than the publisher?',
    answer: () => (
      <>
        Publishers give authorized resellers like us wholesale rates, and we pass most of the
        difference to you. We also keep our costs low: a small team, no call center, no storefronts.
      </>
    ),
  },
  {
    question: 'Will my subscription renew automatically?',
    answer: (urls) => (
      <>
        Yes. Magazine subscriptions renew through DiscountLock so you never miss an issue, at a
        locked-in discount. We email you 30 days and 7 days before any renewal, and you can turn
        DiscountLock off for any subscription in{' '}
        <a className={LINK} href={urls.manageDiscountLock}>
          your account
        </a>{' '}
        at any time. Details are on the{' '}
        <a className={LINK} href={urls.discountLock}>
          DiscountLock page
        </a>
        .
      </>
    ),
  },
  {
    question: 'How long until my first issue arrives?',
    answer: (urls) => (
      <>
        Publishers control printing and mailing, so first issues usually take 4 to 12 weeks
        depending on how often the magazine comes out. Our{' '}
        <a className={LINK} href={urls.delivery}>
          delivery estimates page
        </a>{' '}
        has the timing by frequency. Digital subscriptions are delivered to your Zinio or Magzter
        account right away.
      </>
    ),
  },
  {
    question: 'How do I reach a person?',
    answer: (urls) => (
      <>
        Send us a message through{' '}
        <a className={LINK} href={urls.contact}>
          Contact Us
        </a>{' '}
        with your order number or the email you ordered with. Automated order help is available
        24/7; live agents are available Monday through Friday, {liveAgentClock()} CST.
      </>
    ),
  },
];

/** One cell of the strip pulled up over the hero — a figure, a caption, and a link. */
function GlanceCell({
  href,
  figure,
  children,
  index,
}: {
  href: string;
  figure: ReactNode;
  children: ReactNode;
  index: number;
}) {
  return (
    <li
      className={cn(
        'border-0 border-solid border-slate-200 md:border-t-0',
        // Dividers between cells: two columns on a phone, four above it.
        index % 2 === 1 && 'border-l',
        index >= 2 && 'border-t',
        index > 0 && 'md:border-l'
      )}
    >
      <a
        href={href}
        className="block h-full px-5 py-4 text-slate-500 no-underline hover:bg-slate-50"
      >
        <span className="block text-xl font-extrabold leading-tight tracking-tight text-slate-900 [font-variant-numeric:tabular-nums]">
          {figure}
        </span>
        <span className="mt-1 block text-xs leading-snug">{children}</span>
      </a>
    </li>
  );
}

/**
 * One of the four tiles under "Why we're different".
 *
 * The icon is a FontAwesome definition rather than the literal character the mockup used.
 * Two of those four characters — U+263A and U+21A9 — have *emoji* presentation by default,
 * so the font stack resolved them out of the colour-emoji font: a grey smiley and a blue
 * arrow next to a red `$` and a red tick, differing again on every operating system. A
 * glyph that is meant to take the brand colour has to come from an icon set, not from the
 * text stack.
 */
function DifferenceTile({
  icon,
  heading,
  children,
  href,
  cta,
}: {
  /**
   * `IconProp`, not `IconDefinition`, and cast at every call site — the pro packages
   * bring their own copy of `fontawesome-common-types`, so the definition a caller
   * imports is a structurally identical type from a different file and tsc rejects it.
   * `BulkOrders/constants` does the same for the same reason.
   */
  icon: IconProp;
  heading: string;
  children: ReactNode;
  href: string;
  cta: string;
}) {
  return (
    <div className="flex flex-col rounded-xl border border-solid border-slate-200 bg-white p-5">
      <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-lg text-primary">
        <FontAwesomeIcon icon={icon} aria-hidden />
      </span>

      <h3 className="m-0 mb-1.5 text-lg font-bold leading-snug text-slate-900">{heading}</h3>
      <p className="mb-3 mt-0 text-sm text-slate-800">{children}</p>

      <a className={cn(LINK, 'mt-auto text-sm font-bold')} href={href}>
        {cta} &rarr;
      </a>
    </div>
  );
}

/**
 * /about-us.
 *
 * The copy is the content owner's, not this component's: it comes from the signed-off
 * mockup on the task and may not be re-worded, re-ordered or re-punctuated without a task
 * that supplies the new wording.
 *
 * The design is /reviews' and /guarantee's — the same gradient hero with a stat strip
 * pulled up over it, and the shared ContentCard for the sections below. The mockup shipped
 * its own stylesheet scoped under an `.about-page` wrapper; it is expressed here as
 * utilities instead, which scopes it harder (nothing is emitted outside the elements that
 * ask for it) and, more importantly, keeps the palette themeable. The mockup pinned its
 * reds as literals on `:root`, which is not scoped at all and would have hardcoded
 * DiscountMags' red into a tree four brands render.
 *
 * Two figures on this page are deliberately not typed as copy:
 *
 *  - **The years in the H1**, counted from 2002 so it never has to be edited. The year it
 *    counts to is the server's, handed to `useCurrentYear`: the payload the browser
 *    hydrates from carries the same year the markup does, so the two agree, and the hook
 *    corrects the number after that first render if the cached payload has turned stale.
 *  - **`reviews`**, read through the same client and the same reading /reviews uses. This
 *    page links straight there, so the two quoting different numbers is the site
 *    contradicting itself one click apart. When SmartCustomer cannot be reached the strip
 *    says "Verified" and quotes nothing — never a zero.
 */
export default function AboutUs({ reviews, currentYear }: Props) {
  const { buildUrl } = useUrlGenerator();
  const yearsInBusiness = useCurrentYear(currentYear) - FOUNDED_YEAR;

  const urls = {
    guarantee: buildUrl('/guarantee'),
    reviews: buildUrl('/reviews'),
    bestsellers: buildUrl('/bestsellers'),
    contact: buildUrl('/contact'),
    refundPolicy: buildUrl('/refund-policy'),
    delivery: buildUrl('/shipping'),
    discountLock: buildUrl('/discountlock'),
    manageDiscountLock: buildUrl('/my-account/discountlock'),
    digital: buildUrl('/categories/digital'),
    books: buildUrl('/categories/books'),
    account: buildUrl('/customer?xCmd=account'),
    bulkOrder: buildUrl('/bulk-order'),
    affiliates: buildUrl('/affiliate-program'),
  };

  return (
    <div className="text-slate-800">
      {/* The page's own trail, not the layout's. */}
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'About Us' }]} />

      <PageHeading
        hero
        eyebrow="About DiscountMags"
        intro={
          <>
            If you&apos;re reading this, you&apos;re probably wondering who we are and whether
            it&apos;s safe to buy from us. The answer is yes. We aren&apos;t a publishing
            conglomerate or a faceless marketplace. We&apos;re a family owned business based in the
            United States, and we&apos;ve been selling magazine subscriptions the same way since
            2002: lowest price, no confusion, and a{' '}
            <a className="text-white underline underline-offset-2" href={urls.guarantee}>
              guarantee
            </a>{' '}
            we actually stand behind.
          </>
        }
      >
        A family business, {yearsInBusiness} years in the making
      </PageHeading>

      {/* Pulled up over the bottom of the hero, which is padded out to leave room for it.
          `relative` is load-bearing: the hero is positioned, and a static strip would be
          painted behind it and lose its top rows. */}
      <ul
        aria-label="DiscountMags at a glance"
        className={cn(
          GUTTER,
          'relative -mt-16 grid list-none grid-cols-2 overflow-hidden rounded-xl',
          'border border-solid border-slate-200 bg-white p-0 md:grid-cols-4'
        )}
      >
        <GlanceCell index={0} href="#story" figure="2002">
          Family owned and run <b className="font-bold text-primary">since day one</b>
        </GlanceCell>

        {/* The review cell. Its figure is the live one or none at all — see the Props
            docblock; "Verified" is what an unreachable SmartCustomer looks like. */}
        {reviews ? (
          <GlanceCell
            index={1}
            href={urls.reviews}
            figure={`${reviews.averageRating.toFixed(1)}/5`}
          >
            From {reviews.reviewCount.toLocaleString('en-US')} reviews{' '}
            <b className="font-bold text-primary">independently collected</b> by SmartCustomer
          </GlanceCell>
        ) : (
          <GlanceCell index={1} href={urls.reviews} figure="Verified">
            Reviews <b className="font-bold text-primary">independently collected</b> by
            SmartCustomer
          </GlanceCell>
        )}

        <GlanceCell index={2} href={urls.bestsellers} figure="1,000,000+">
          magazines &amp; books, <b className="font-bold text-primary">free shipping</b> on
          magazines
        </GlanceCell>

        <GlanceCell index={3} href={urls.guarantee} figure="6 months">
          to cancel any subscription for a{' '}
          <b className="font-bold text-primary">pro-rated refund</b>
        </GlanceCell>
      </ul>

      <nav
        aria-label="On this page"
        className="mt-7 flex flex-wrap items-center gap-2"
      >
        <span className="text-sm font-semibold text-slate-500">On this page:</span>
        {JUMP_LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="rounded-full border border-solid border-slate-200 px-3.5 py-1 text-sm font-semibold text-slate-800 no-underline hover:border-primary hover:text-primary"
          >
            {link.label}
          </a>
        ))}
      </nav>

      <ContentCard id="story" className={cn(CARD, BLOCK, ANCHORED)}>
        <h2 className={SECTION_HEADING}>Our story</h2>
        <p className={TAGLINE}>
          Started in 2002 to fix an industry that made buying a magazine harder than it needed to
          be.
        </p>

        <p className={BODY}>
          We started DiscountMags in 2002 with a simple mission: offer the absolute lowest prices on
          magazine subscriptions without the confusion that plagues this industry. Back then, buying
          a subscription meant filling out a card that fell out of a magazine, mailing it in, and
          waiting months to find out whether it worked. We knew there had to be a better way.
        </p>

        <p className={BODY}>
          Today we&apos;re one of the largest magazine subscription retailers in the country, and
          we&apos;ve grown into{' '}
          <a className={LINK} href={urls.digital}>
            digital magazines
          </a>{' '}
          (2016) and{' '}
          <a className={LINK} href={urls.books}>
            books
          </a>{' '}
          (2023) too. What hasn&apos;t changed is how we work: a small team, no middle management,
          and the same family running things.
        </p>

        <ol className="m-0 mt-4 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-3">
          {TIMELINE.map((entry) => (
            <li key={entry.year} className={TILE}>
              <span className={cn(TILE_LABEL, 'block text-primary')}>{entry.year}</span>
              <span className="mt-0.5 block text-[15px] font-bold text-slate-900">
                {entry.title}
              </span>
              <span className="mt-1 block text-sm leading-snug text-slate-500">{entry.detail}</span>
            </li>
          ))}
        </ol>
      </ContentCard>

      <ContentCard id="different" className={cn(CARD, BLOCK, ANCHORED)}>
        <h2 className={SECTION_HEADING}>Why we&apos;re different</h2>
        <p className={TAGLINE}>Being small is the advantage. Here&apos;s what it gets you.</p>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <DifferenceTile
            icon={faDollarSign as IconProp}
            heading="The lowest prices, and we keep negotiating"
            href={urls.guarantee}
            cta="See our price match guarantee"
          >
            We already offer the lowest prices on thousands of magazines, and we never stop pushing
            publishers for better. If you find the same subscription for less from an authorized
            seller, we&apos;ll match it.
          </DifferenceTile>

          <DifferenceTile
            icon={faFaceSmile as IconProp}
            heading="Real people on the other end"
            href={urls.contact}
            cta="Contact us"
          >
            Automated order help is available around the clock, and live agents are here Monday
            through Friday, {liveAgentClock()} CST. Every message you send is read by someone on our team,
            not routed into a void.
          </DifferenceTile>

          <DifferenceTile
            icon={faArrowRotateLeft as IconProp}
            heading="Easy cancellation and refunds"
            href={urls.refundPolicy}
            cta="Read the refund policy"
          >
            Not happy with a subscription? Cancel within 6 months of ordering and we&apos;ll refund
            you for the issues that haven&apos;t been sent yet. Any reason, or no reason at all.
          </DifferenceTile>

          <DifferenceTile
            icon={faCheck as IconProp}
            heading="You're always in control"
            href={urls.discountLock}
            cta="How DiscountLock works"
          >
            Magazine subscriptions renew through DiscountLock, which keeps your subscription from
            lapsing at a locked-in discount, and we email you 30 days and 7 days before every
            renewal. Don&apos;t want it? Turn it off for any subscription in your account, any time,
            with one click.
          </DifferenceTile>
        </div>
      </ContentCard>

      <ContentCard id="team" className={cn(CARD, BLOCK, ANCHORED)}>
        <h2 className={SECTION_HEADING}>Our team</h2>
        <p className={TAGLINE}>Small on purpose.</p>

        <ul className="m-0 mb-4 mt-3.5 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-3">
          {TEAM_FACTS.map((fact) => (
            <li key={fact.label} className={TILE}>
              <span className={cn(TILE_LABEL, 'block text-slate-500')}>{fact.label}</span>
              <span className="mt-0.5 block text-lg font-extrabold tracking-tight text-slate-900">
                {fact.value}
              </span>
              <span className="mt-0.5 block text-xs leading-snug text-slate-500">{fact.note}</span>
            </li>
          ))}
        </ul>

        <p className={BODY}>
          There are about a dozen of us, and the owners still work customer tickets alongside
          everyone else. That&apos;s not a hardship, it&apos;s the point: when the people making
          decisions are the same people reading your email, problems get fixed instead of filed.
        </p>

        <p className={BODY}>
          We treat our team the way we want to be treated, and we&apos;d rather keep a small group
          of people who know this business inside out than churn through a call center. When you
          write to us, you&apos;re talking to someone who has probably handled your exact question a
          hundred times.
        </p>
      </ContentCard>

      <ContentCard id="promise" className={cn(CARD, BLOCK, ANCHORED)}>
        <h2 className={SECTION_HEADING}>Our promise</h2>

        <p className={cn(BODY, 'mt-3')}>
          When you order from us, you aren&apos;t just an order ID. You&apos;re supporting a small,
          independent business that cares about reading as much as you do, and you&apos;re getting
          the same thing every customer has gotten since 2002: the lowest price we can find, a{' '}
          <a className={LINK} href={urls.guarantee}>
            guarantee
          </a>{' '}
          that means something, and a person who will help when something goes wrong.
        </p>

        <p className={BODY}>
          Publishers still own printing and mailing, so we can&apos;t promise a first issue arrives
          faster than the industry allows (our{' '}
          <a className={LINK} href={urls.delivery}>
            delivery estimates
          </a>{' '}
          are honest about that). What we can promise is that if it doesn&apos;t arrive, we&apos;ll
          chase it down for you.
        </p>

        <p className="mb-0 mt-4 border-0 border-t border-solid border-slate-200 pt-4 text-[15px]">
          Thank you for choosing us.
          <b className="block text-slate-900">The DiscountMags Team</b>
        </p>
      </ContentCard>

      <ContentCard id="contact" className={cn(CARD, BLOCK, ANCHORED)}>
        <h2 className={SECTION_HEADING}>Get in touch</h2>
        <p className={TAGLINE}>
          Questions about an order, a charge, or a missing issue? Start here.
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <ButtonLink href={urls.contact} className="border border-solid border-primary">
            Contact Customer Care
          </ButtonLink>
          {/* The ghost of the same button, so the set shares a box — as on /guarantee. */}
          <ButtonLink
            href={buildUrl('/help')}
            className="border border-solid border-primary bg-white text-primary hover:bg-primary hover:text-white"
          >
            Browse the Help Center
          </ButtonLink>
          <ButtonLink
            href={urls.account}
            className="border border-solid border-primary bg-white text-primary hover:bg-primary hover:text-white"
          >
            My Account
          </ButtonLink>
        </div>

        <p className="mb-0 mt-3.5 text-sm text-slate-500">
          <b className="text-slate-900">Automated order support:</b> available 24/7 &nbsp;·&nbsp;{' '}
          <b className="text-slate-900">Live agents:</b> {liveAgentHours()}
        </p>

        <p className="mb-0 mt-3.5 text-sm text-slate-800">
          Want to buy for a school, office or waiting room? See{' '}
          <a className={LINK} href={urls.bulkOrder}>
            bulk orders
          </a>
          . Run a website? Our{' '}
          <a className={LINK} href={urls.affiliates}>
            affiliate program
          </a>{' '}
          pays on every sale.
        </p>
      </ContentCard>

      <aside
        className={cn(
          BLOCK,
          'flex items-start gap-3.5 rounded-xl border border-solid border-primary-200 bg-primary-50 p-5'
        )}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-lg text-white">
          <FontAwesomeIcon icon={faStar as IconProp} aria-hidden />
        </span>

        <div>
          <b className="block text-slate-900">Don&apos;t take our word for it.</b>
          <p className="mb-0 mt-1 max-w-[64ch] text-sm text-slate-800">
            Every review on our{' '}
            <a className={LINK} href={urls.reviews}>
              reviews page
            </a>{' '}
            is collected and verified independently by SmartCustomer from confirmed customers. We
            can&apos;t edit, reorder or remove them, so what you see is what people actually said.
          </p>
        </div>
      </aside>

      <ContentCard id="faqs" className={cn(CARD, BLOCK, ANCHORED)}>
        <h2 className={cn(SECTION_HEADING, 'mb-2')}>Questions people ask before ordering</h2>

        {/* Native `details`, as the mockup has them: the answers are in the document
            whether or not javascript ran, which on a page shoppers reach from a search for
            "is discountmags legit" is the point. The shared FaqAccordion is a heavier
            treatment that belongs to /reviews. */}
        {FAQS.map((faq, index) => (
          <details
            key={faq.question}
            open={index === 0}
            className="group border-0 border-b border-solid border-slate-200 py-1 last:border-b-0"
          >
            <summary className="relative cursor-pointer list-none py-3 pr-7 text-[15px] font-bold text-slate-900 [&::-webkit-details-marker]:hidden">
              {faq.question}
              <span
                aria-hidden
                className="absolute right-1 top-2.5 text-xl font-semibold leading-none text-primary"
              >
                <span className="group-open:hidden">+</span>
                <span className="hidden group-open:inline">&ndash;</span>
              </span>
            </summary>

            <div className="max-w-[70ch] pb-4 text-sm text-slate-800">{faq.answer(urls)}</div>
          </details>
        ))}
      </ContentCard>

      <p className="mb-0 mt-6 max-w-[80ch] text-xs leading-relaxed text-slate-500">
        This site is not officially affiliated with, associated with, or endorsed by any magazine or
        publisher. Magazine names, logos, and images may be registered trademarks of individual
        magazines, publishers, or parent companies.
      </p>
    </div>
  );
}

AboutUs.layoutName = 'Layouts.Institutional';
